import { Box, Flex, useColorModeValue } from '@chakra-ui/react';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import React, { ReactNode } from 'react';
import { TimelineDot } from './TimelineDot';

interface TimelineEventWrapperProps {
  children: ReactNode;
  isSelected: boolean;
  isInPreview: boolean;
  isDragging: boolean;
  isFirstInSelection: boolean;
  isLastInSelection: boolean;
  isFirstInPreview: boolean;
  isLastInPreview: boolean;
  icon: IconDefinition;
  isFirstEvent?: boolean;
  isHovered?: boolean;
}

// Helper functions to calculate styles
const calculateBackgroundColor = (
  isDragging: boolean,
  isInPreview: boolean,
  isSelected: boolean,
  selectedBgColor: string,
  previewBgColor: string
): string | undefined => {
  if (isDragging) {
    return isInPreview ? previewBgColor : undefined;
  }
  return isSelected ? selectedBgColor : undefined;
};

const calculateBorderStyles = (
  isDragging: boolean,
  isFirstInSelection: boolean,
  isLastInSelection: boolean,
  isFirstInPreview: boolean,
  isLastInPreview: boolean,
  isSelected: boolean
) => {
  let borderTopRadius = '0';
  let borderTop = 'none';
  let borderBottomRadius = '0';
  let borderBottom = 'none';

  if (isDragging) {
    if (isFirstInPreview) {
      borderTopRadius = 'lg';
      borderTop = '2px solid';
    }
    if (isLastInPreview) {
      borderBottomRadius = 'lg';
      borderBottom = '2px solid';
    }
  } else {
    if (isFirstInSelection && isSelected) {
      borderTopRadius = 'lg';
      borderTop = '2px solid';
    }
    if (isLastInSelection && isSelected) {
      borderBottomRadius = 'lg';
      borderBottom = '2px solid';
    }
  }

  return {
    borderTopRadius,
    borderTop,
    borderBottomRadius,
    borderBottom,
  };
};

// eslint-disable-next-line complexity
export const TimelineEventWrapper: React.FC<TimelineEventWrapperProps> = ({
  children,
  isSelected,
  isInPreview,
  isDragging,
  isFirstInSelection,
  isLastInSelection,
  isFirstInPreview,
  isLastInPreview,
  icon,
  isFirstEvent = false,
  isHovered = false,
}) => {
  // Colors
  const selectedBgColor = useColorModeValue('blue.50', 'blue.800');
  const previewBgColor = useColorModeValue('gray.50', 'gray.700');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const selectedBorderColor = useColorModeValue('blue.500', 'blue.300');

  // Calculate styles
  const bgColor = calculateBackgroundColor(
    isDragging,
    isInPreview,
    isSelected,
    selectedBgColor,
    previewBgColor
  );

  // Update border styles logic for the first event
  const getBorderStyles = () => {
    // If this is the first event, never use top radius or top border,
    // as those should be on the CurrentVersionHeader
    if (isFirstEvent) {
      const { borderBottomRadius, borderBottom } = calculateBorderStyles(
        isDragging,
        isFirstInSelection,
        isLastInSelection,
        isFirstInPreview,
        isLastInPreview,
        isSelected
      );

      return {
        borderTopRadius: '0',
        borderTop: 'none',
        borderBottomRadius,
        borderBottom,
        borderLeft: isSelected || isInPreview ? '2px solid' : 'none',
        borderRight: isSelected || isInPreview ? '2px solid' : 'none',
      };
    }

    // For other events, use the normal calculation
    const { borderTopRadius, borderTop, borderBottomRadius, borderBottom } =
      calculateBorderStyles(
        isDragging,
        isFirstInSelection,
        isLastInSelection,
        isFirstInPreview,
        isLastInPreview,
        isSelected
      );

    return {
      borderTopRadius,
      borderTop,
      borderBottomRadius,
      borderBottom,
      borderLeft: isSelected || isInPreview ? '2px solid' : 'none',
      borderRight: isSelected || isInPreview ? '2px solid' : 'none',
    };
  };

  const { borderTopRadius, borderTop, borderBottomRadius, borderBottom } =
    getBorderStyles();

  return (
    <Flex position="relative">
      {/* Event dot */}
      <Box
        position="absolute"
        left="16px"
        top="12px"
        transform="translateX(-50%)"
        zIndex={2}
      >
        <TimelineDot isSelected={isSelected} icon={icon} />
      </Box>

      {/* Event content */}
      <Box
        flex="1"
        backgroundColor={
          isHovered && !isSelected && !isInPreview ? hoverBgColor : bgColor
        }
        borderTopRadius={borderTopRadius}
        paddingTop={borderTopRadius === '0' ? '2px' : 0}
        borderTop={borderTop}
        borderBottomRadius={
          isHovered && !isSelected && !isInPreview ? 'lg' : borderBottomRadius
        }
        paddingBottom={borderBottomRadius === '0' ? '2px' : 0}
        paddingLeft={5}
        borderBottom={borderBottom}
        borderColor={selectedBorderColor}
        _hover={
          !isDragging && !isSelected && !isHovered
            ? { backgroundColor: hoverBgColor, borderRadius: 'md' }
            : undefined
        }
        transition="background-color 0.2s"
      >
        {children}
      </Box>
    </Flex>
  );
};

export default TimelineEventWrapper;
